import { z } from "zod";

export const storageProviderSchema = z.enum(["qiniu", "cos", "oss", "obs"]);
export type StorageProvider = z.infer<typeof storageProviderSchema>;
export const storageProviderLabels: Record<StorageProvider, string> = { qiniu: "七牛云 Kodo", cos: "腾讯云 COS", oss: "阿里云 OSS", obs: "华为云 OBS" };
export const qiniuS3Regions = { "cn-east-1": "华东浙江", "cn-east-2": "华东浙江 2", "cn-north-1": "华北河北", "cn-south-1": "华南广东", "us-north-1": "北美洛杉矶", "ap-southeast-1": "亚太新加坡", "ap-southeast-2": "亚太河内", "ap-southeast-3": "亚太胡志明" } as const;
const secret = z.string().trim().min(1).max(256).regex(/^[\x21-\x7e]+$/);
export const storageCredentialsSchema = z.object({ accessKeyId: secret, accessKeySecret: secret }).strict();
export const storageLocationSchema = z.object({
  provider: storageProviderSchema,
  bucket: z.string().trim().min(3).max(128).regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/),
  region: z.string().trim().max(64).regex(/^[a-z0-9-]*$/),
  prefix: z.string().trim().max(128).regex(/^(?:[a-zA-Z0-9_-]+\/)*$/),
  downloadDomain: z.string().trim().max(253).refine(value => !value || /^https:\/\/[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i.test(value), "请填写不带路径和端口的 HTTPS 域名"),
}).strict().superRefine((value, ctx) => {
  if (value.provider === "qiniu" && !value.downloadDomain && !Object.hasOwn(qiniuS3Regions, value.region)) ctx.addIssue({ code: "custom", path: ["region"], message: "七牛使用官方 HTTPS 读取时请选择 S3 区域" });
  if (value.provider !== "qiniu" && !value.region) ctx.addIssue({ code: "custom", path: ["region"], message: "请选择存储区域" });
  if (value.provider === "cos" && !/^.+-\d{5,}$/.test(value.bucket)) ctx.addIssue({ code: "custom", path: ["bucket"], message: "COS 桶名需要包含 APPID 后缀" });
});
export type StorageLocation = z.infer<typeof storageLocationSchema>;
export const storageCreateSchema = z.object({ name: z.string().trim().min(1).max(64), location: storageLocationSchema, credentials: storageCredentialsSchema }).strict();
export const storageUpdateSchema = z.object({ name: z.string().trim().min(1).max(64), credentials: storageCredentialsSchema.optional(), expectedVersion: z.number().int().positive() }).strict();
export const storageVersionSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const storageDefaultSchema = z.object({ configId: z.uuid().nullable(), expectedStateVersion: z.number().int().positive() }).strict();
export const storageConfigurationSchema = z.object({
  id: z.uuid(), name: z.string(), location: storageLocationSchema, version: z.number().int().positive(),
  credentialsConfigured: z.boolean(), testedVersion: z.number().int().positive().nullable(),
  testedAt: z.string().nullable(), createdAt: z.string(), updatedAt: z.string(),
}).strict();
export type StorageConfiguration = z.infer<typeof storageConfigurationSchema>;
export const storageStateSchema = z.object({ items: z.array(storageConfigurationSchema), defaultConfigId: z.uuid().nullable(), version: z.number().int().positive() }).strict();
export type StorageState = z.infer<typeof storageStateSchema>;
export const storageTestResultSchema = z.object({ ok: z.literal(true), message: z.string() }).strict();

export type StorageCreateInput = z.infer<typeof storageCreateSchema>;
export type StorageUpdateInput = z.infer<typeof storageUpdateSchema>;

const limit = z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable();
export const storageBalanceThresholdSchema = z.string().regex(/^\d{1,12}(?:\.\d{1,2})?$/, '请输入非负金额，最多两位小数').nullable();
export const storageLimitsSchema = z.object({
  maxBytes: limit, maxObjects: limit, monthlyUploadBytes: limit,
  alertPercent: z.number().int().min(1).max(100),
  balanceAlertThreshold: storageBalanceThresholdSchema.default(null),
  balanceAlertCurrency: z.enum(['CNY', 'USD']).default('CNY'),
}).strict();
export const storagePolicyInputSchema = storageLimitsSchema.extend({ expectedVersion: z.number().int().positive() }).strict();
export type StorageLimits = z.infer<typeof storageLimitsSchema>;
export type StoragePolicyInput = z.infer<typeof storagePolicyInputSchema>;
export const storageMetricSchema = z.object({
  key: z.string(), label: z.string(), value: z.string().nullable(), unit: z.enum(["bytes", "MB", "count"]), observedAt: z.string().nullable(),
}).strict();
export const storageVendorUsageSchema = z.object({
  status: z.enum(["ok", "partial", "error"]), fetchedAt: z.string(), scope: z.string(), note: z.string(),
  metrics: z.array(storageMetricSchema),
  quota: z.object({ status: z.enum(["ok", "unavailable", "not_integrated"]), maxBytes: z.string().nullable(), note: z.string() }).strict(),
}).strict();
export type StorageVendorUsage = z.infer<typeof storageVendorUsageSchema>;
export const storageUsageSchema = z.object({
  configId: z.uuid(), configVersion: z.number().int().positive(),
  policy: storageLimitsSchema.extend({ version: z.number().int().positive() }).strict(),
  platform: z.object({ bytes: z.number().nonnegative(), objects: z.number().int().nonnegative(), unknownSizeObjects: z.number().int().nonnegative(), monthlyUploadBytes: z.number().nonnegative(), month: z.string(), alerts: z.array(z.string()) }).strict(),
  vendor: storageVendorUsageSchema.nullable(),
}).strict();
export type StorageUsage = z.infer<typeof storageUsageSchema>;

const financeAmount = z.string().regex(/^-?\d+(?:\.\d+)?$/).nullable();
const financeStatus = z.enum(["ok", "partial", "permission_required", "error", "unsupported"]);
export const storageFinanceSnapshotSchema = z.object({
  fetchedAt: z.string(), scope: z.string(),
  balance: z.object({
    status: financeStatus, note: z.string(), currency: z.enum(["CNY", "USD"]).nullable(),
    available: financeAmount, cash: financeAmount, present: financeAmount, credit: financeAmount,
    unpaid: financeAmount, estimated: financeAmount,
  }).strict(),
  monthly: z.object({
    status: financeStatus, note: z.string(), month: z.string(), truncated: z.boolean(),
    items: z.array(z.object({
      item: z.string(), region: z.string(), period: z.string(), unit: z.string(),
      total: financeAmount, used: financeAmount, remaining: financeAmount,
    }).strict()).max(200),
  }).strict(),
  packages: z.object({
    status: financeStatus, note: z.string(), truncated: z.boolean(),
    items: z.array(z.object({
      name: z.string(), status: z.number().int().nullable(), allocation: z.number().int().nullable(),
      startsAt: z.string().nullable(), expiresAt: z.string().nullable(), updatedAt: z.string().nullable(),
      total: financeAmount, used: financeAmount, unit: z.string(),
    }).strict()).max(200),
  }).strict(),
}).strict();
export const storageFinanceSchema = z.object({
  configId: z.uuid(), configVersion: z.number().int().positive(),
  snapshot: storageFinanceSnapshotSchema.nullable(),
}).strict();
export type StorageFinanceSnapshot = z.infer<typeof storageFinanceSnapshotSchema>;
export type StorageFinance = z.infer<typeof storageFinanceSchema>;
